import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Camera, Plus, Trash2, Bone, Cookie, Utensils, Sparkles, ShoppingCart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, assetUrl, errMsg } from "@/lib/api";

export const ITEM_CATS = {
  food: { label: "Food", icon: Utensils, color: "#D97706", bg: "#FFF7E6" },
  treat: { label: "Treat", icon: Cookie, color: "#E07A5F", bg: "#FDF2F0" },
  toy: { label: "Toy", icon: Bone, color: "#10B981", bg: "#ECFDF5" },
  other: { label: "Other", icon: Sparkles, color: "#475569", bg: "#F1F5F9" },
};

export function ItemCard({ item, onDelete, onLow }) {
  const c = ITEM_CATS[item.category] || ITEM_CATS.other;
  return (
    <div className="pl-card overflow-hidden" data-testid={`pet-item-${item.id}`}>
      <div className="aspect-square bg-[#FAF7F2] grid place-items-center overflow-hidden">
        {item.photo_url ? <img src={assetUrl(item.photo_url)} alt={item.name} className="h-full w-full object-cover" loading="lazy" />
          : <c.icon className="h-10 w-10" style={{ color: c.color }} />}
      </div>
      <div className="p-3">
        <span className="pl-chip !px-2 !py-0.5 text-[10px]" style={{ background: c.bg, color: c.color }}><c.icon className="h-3 w-3" />{c.label}</span>
        <p className="font-semibold leading-snug mt-1">{item.name}</p>
        {item.notes && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{item.notes}</p>}
        {onLow && <button onClick={() => onLow(item)} data-testid={`pet-item-low-${item.id}`} className="mt-2 w-full rounded-full bg-[#FFF7E6] text-[#B45309] text-xs font-bold py-2 flex items-center justify-center gap-1 active:scale-95 transition-transform"><ShoppingCart className="h-3.5 w-3.5" />Running low</button>}
        {onDelete && <button onClick={() => onDelete(item)} data-testid={`pet-item-delete-${item.id}`} className="mt-2 text-xs text-slate-400 font-semibold flex items-center gap-1"><Trash2 className="h-3 w-3" />Remove</button>}
      </div>
    </div>
  );
}

function AddItemDialog({ open, onOpenChange, petId, onSaved }) {
  const [f, setF] = useState({ name: "", category: "food", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
    if (file) fd.append("file", file);
    try {
      await api.post(`/pets/${petId}/items`, fd);
      toast.success(`${f.name} saved`);
      setF({ name: "", category: "food", notes: "" });
      setFile(null);
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-[#FAF7F2] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Add a favorite</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="pet-item-form">
          <label className="block rounded-2xl border-2 border-dashed border-[#F59E0B] bg-white overflow-hidden cursor-pointer" data-testid="pet-item-photo-picker">
            {preview ? <img src={preview} alt="Preview" className="h-48 w-full object-cover" />
              : <div className="h-36 grid place-items-center text-center"><div><Camera className="h-7 w-7 mx-auto text-[#D97706]" /><p className="font-semibold mt-1">Snap the bag, can or treat</p><p className="text-xs text-slate-500">So anyone can buy the right one</p></div></div>}
            <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="pet-item-photo-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ITEM_CATS).map(([k, c]) => (
              <button type="button" key={k} data-testid={`pet-item-cat-${k}`} onClick={() => setF({ ...f, category: k })}
                className={`pl-chip !py-2 border ${f.category === k ? "border-[#1E293B]" : "border-transparent"}`} style={{ background: c.bg, color: c.color }}><c.icon className="h-3.5 w-3.5" />{c.label}</button>
            ))}
          </div>
          <div><label className="pl-label">Name *</label><input required className="pl-input" placeholder="e.g. Blue Buffalo Salmon kibble" data-testid="pet-item-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="pl-label">Notes</label><textarea rows={2} className="pl-input py-3" placeholder="e.g. Only the small-bite version. Max 2 a day." data-testid="pet-item-notes-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <button disabled={busy || !f.name} className="pl-btn-primary w-full" data-testid="pet-item-save-btn">{busy ? "Saving..." : "Save favorite"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PetItems({ petId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get(`/pets/${petId}/items`).then((r) => setItems(r.data)), [petId]);
  useEffect(() => { load(); }, [load]);
  const remove = async (i) => {
    if (!window.confirm(`Remove ${i.name}?`)) return;
    try { await api.delete(`/items/${i.id}`); toast("Removed"); } catch (err) { toast.error(errMsg(err)); }
    load();
  };
  const low = async (i) => {
    try {
      const { data } = await api.post("/shopping", { item_id: i.id });
      toast.success(data.already ? `${i.name} is already on the shopping list` : `${i.name} added to the shopping list`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="pet-items-section">
      <button className="pl-btn-primary w-full mb-4" onClick={() => setOpen(true)} data-testid="pet-item-add-btn"><Plus className="h-4 w-4" />Add food, treat or toy</button>
      {items.length === 0 && <p className="text-sm text-slate-500" data-testid="pet-items-empty">Snap photos of their food, favorite treats and toys. Sitters see them in the care guide.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{items.map((i) => <ItemCard key={i.id} item={i} onDelete={remove} onLow={low} />)}</div>
      <AddItemDialog open={open} onOpenChange={setOpen} petId={petId} onSaved={load} />
    </div>
  );
}
